"use client";

import { useState } from "react";
import { GripVertical, Image as ImageIcon, List, Minus, Plus, SlidersHorizontal, Trash2 } from "lucide-react";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import {
    FlipCard,
    FlipCardsAppearance,
    FlipCardsBlock,
} from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../store/useLomdaV3Editor";
import { useResolvedGridGap } from "../../hooks/useGridGap";
import {
    resolveFlipAppearance,
    FLIP_CARDS_CONFIG,
    createFlipCard,
    flipCardsVariant,
} from "../../lib/blockRegistry";
import { blockLabelKey } from "../../lib/sectionRegistry";
import { DEFAULT_THEME, resolveCardForeground } from "../../lib/theme";
import { TabIcon } from "../../lib/tabIcons";
import LomdaUploadFile from "../blocks/shared/lomdaUploadFile";
import {
    AppearanceDivider,
    AppearanceReset,
    AppearanceRow,
    ColorChipField,
    ToolbarDivider,
    PopoverTitle,
    WidthControl,
    chromeBtn,
    chromeBtnOpen,
} from "./blockAppearance";
import { IMAGE_UPLOAD } from "../../lib/uploadLimits";

export default function FlipCardsSettings({ block }: { block: FlipCardsBlock }) {
    const replaceBlockData = useLomdaV3Editor((s) => s.replaceBlockData);
    const patchBlockData = useLomdaV3Editor((s) => s.patchBlockData);
    const setBlockGridGap = useLomdaV3Editor((s) => s.setBlockGridGap);
    const gridGap = useResolvedGridGap(block.id);

    const { cards } = block.data;
    const variant = flipCardsVariant(block.data);
    const columns = block.data.columns ?? FLIP_CARDS_CONFIG.defaultColumns;
    const theme = useLomdaV3Editor((s) => s.metadata.theme);
    const appearance = resolveFlipAppearance(block.data.appearance, theme);
    const update = (patch: Partial<FlipCardsBlock["data"]>) =>
        replaceBlockData(block.id, { ...block.data, ...patch });
    const setCards = (next: FlipCard[]) => update({ cards: next });

    // Second tier: clicking a card focuses it (same store field the quiz uses
    // for its per-question settings), and its own appearance appears here.
    const focusedCardId = useLomdaV3Editor((s) => s.focusedQuestionId);
    const focusedCard = cards.find((c) => c.id === focusedCardId) ?? null;
    const patchCard = (patch: Partial<FlipCard>) =>
        patchBlockData(block.id, "flip-cards", (d) => ({
            cards: d.cards.map((c) =>
                c.id === focusedCardId ? { ...c, ...patch } : c
            ),
        }));

    return (
        <div className="flex items-center gap-3">
            {/* ── 1. Common block settings ── */}
            <div className="flex items-center gap-3">
                <span className="shrink-0 text-sm !font-semibold text-foreground">
                    {t(blockLabelKey(block))}
                </span>
                <WidthControl blockId={block.id} />
                <div className="flex shrink-0 items-center gap-2">
                    <span className="whitespace-nowrap text-sm text-foreground">
                        {t("lomda-v3-editor.settings.grid-gap")}
                    </span>
                    <div className="flex h-9 w-16 items-center rounded-lg border px-2 text-sm text-foreground">
                        <input
                            type="number"
                            min={0}
                            value={gridGap}
                            onChange={(e) =>
                                setBlockGridGap(
                                    block.id,
                                    Math.max(0, parseInt(e.target.value, 10) || 0)
                                )
                            }
                            className="w-full bg-transparent text-center outline-none"
                        />
                    </div>
                </div>
            </div>

            <ToolbarDivider />

            {/* ── 2. Block-specific settings ── */}
            <div className="flex items-center gap-3">
                <AppearancePopover
                    columns={columns}
                    maxColumns={FLIP_CARDS_CONFIG.maxColumns[variant]}
                    hiddenFields={
                        variant === "static"
                            ? FLIP_CARDS_CONFIG.flipOnlyFields
                            : []
                    }
                    onColumns={(n) => update({ columns: n })}
                    appearance={appearance}
                    onChange={(patch) =>
                        update({ appearance: { ...appearance, ...patch } })
                    }
                    onReset={() => update({ appearance: undefined })}
                />
                <ManageCardsPopover cards={cards} onChange={setCards} />
            </div>

            {/* ── 3. Focused card ── */}
            {focusedCard && (
                <>
                    <ToolbarDivider />
                    <CardAppearancePopover
                        card={focusedCard}
                        blockAppearance={appearance}
                        onChange={(appearancePatch) =>
                            patchCard({
                                appearance: {
                                    ...focusedCard.appearance,
                                    ...appearancePatch,
                                },
                            })
                        }
                        onReset={() => patchCard({ appearance: undefined })}
                    />
                </>
            )}
        </div>
    );
}

/** One card's own colours and image. An unset field shows what the card is
 *  painted with: the block's colour and "Text on cards". */
function CardAppearancePopover({
    card,
    blockAppearance,
    onChange,
    onReset,
}: {
    card: FlipCard;
    blockAppearance: FlipCardsAppearance;
    onChange: (patch: Partial<NonNullable<FlipCard["appearance"]>>) => void;
    onReset: () => void;
}) {
    const userId = useLomdaV3Editor((s) => s.userId);
    const theme = useLomdaV3Editor((s) => s.metadata.theme);
    const textDefault = resolveCardForeground(theme ?? DEFAULT_THEME);
    const [uploadNonce] = useState(() => Date.now().toString(36));

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={cn(chromeBtn, chromeBtnOpen, "gap-1.5 px-3")}
                >
                    <ImageIcon className="size-5" />
                    <span className="text-sm whitespace-nowrap">
                        {t("lomda-v3-editor.flip.card-appearance")}
                    </span>
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-[320px] rounded-[20px] border border-border bg-card p-4 shadow-md"
            >
                <div className="flex flex-col gap-4">
                    <PopoverTitle>
                        {t("lomda-v3-editor.flip.card-appearance")}
                    </PopoverTitle>

                    <AppearanceRow
                        label={t("lomda-v3-editor.flip.appearance-card-bg")}
                    >
                        <ColorChipField
                            value={card.appearance?.cardBackground ?? blockAppearance.cardBackground}
                            onChange={(c) => onChange({ cardBackground: c })}
                        />
                    </AppearanceRow>

                    <AppearanceRow label={t("lomda-v3-editor.format.color")}>
                        <ColorChipField
                            value={card.appearance?.textColor ?? textDefault}
                            onChange={(c) => onChange({ textColor: c })}
                        />
                    </AppearanceRow>

                    <AppearanceDivider />

                    <span className="text-sm text-foreground">
                        {t("lomda-v3-editor.flip.card-bg-image")}
                    </span>
                    {card.appearance?.backgroundImage ? (
                        <>
                        <div className="flex items-center gap-2">
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                                src={card.appearance.backgroundImage}
                                alt=""
                                className="h-14 w-20 rounded-lg border object-cover"
                            />
                            <button
                                type="button"
                                onClick={() =>
                                    onChange({ backgroundImage: undefined })
                                }
                                className="text-sm flex items-center gap-1.5 text-muted-foreground transition hover:text-destructive"
                            >
                                <Trash2 className="size-4" />
                                {t("lomda-v3-editor.flip.card-bg-remove")}
                            </button>
                        </div>
                        {/* Same overlay control the lomda background uses:
                            darkens a busy photo so the card text stays legible. */}
                        <AppearanceRow
                            label={t("brand-kits.backgrounds.scrim")}
                        >
                            <div className="flex items-center gap-2">
                                <Slider
                                    min={0}
                                    max={70}
                                    step={5}
                                    value={[
                                        Math.round(
                                            (card.appearance?.scrim ?? 0) * 100
                                        ),
                                    ]}
                                    onValueChange={([v]) =>
                                        onChange({
                                            scrim: v > 0 ? v / 100 : undefined,
                                        })
                                    }
                                    className="w-32"
                                />
                                <span
                                    dir="ltr"
                                    className="w-9 text-end text-sm text-muted-foreground"
                                >
                                    {Math.round(
                                        (card.appearance?.scrim ?? 0) * 100
                                    )}
                                    %
                                </span>
                            </div>
                        </AppearanceRow>
                        </>
                    ) : (
                        userId && (
                            <LomdaUploadFile
                                reuse="image"
                                folder={`card-backgrounds/${uploadNonce}/${card.id}`}
                                options={{
                                    ...IMAGE_UPLOAD,
                                    showImagePreview: false,
                                    showSelectedFileName: false,
                                    purgeCache: true,
                                    className: "h-20 w-full",
                                    title: {
                                        text: t("lomda-v3-editor.flip.card-bg-upload"),
                                        keyName: "lomda-v3-editor.flip.card-bg-upload",
                                    },
                                    subtitle: {
                                        text: "PNG, JPG, WEBP",
                                        keyName: "brand-kits.backgrounds.upload-subtitle",
                                    },
                                }}
                                onUploadFinished={(f) =>
                                    onChange({ backgroundImage: f.fileUrl })
                                }
                            />
                        )
                    )}

                    <AppearanceReset onReset={onReset} />
                </div>
            </PopoverContent>
        </Popover>
    );
}

function ManageCardsPopover({
    cards,
    onChange,
}: {
    cards: FlipCard[];
    onChange: (cards: FlipCard[]) => void;
}) {
    const [dragIndex, setDragIndex] = useState<number | null>(null);

    const move = (from: number, to: number) => {
        if (from === to) return;
        const next = [...cards];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        onChange(next);
    };

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button type="button" className={cn(chromeBtn, chromeBtnOpen)}>
                    <List className="size-5" />
                    {t("lomda-v3-editor.flip.manage-cards")}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[412px] rounded-2xl p-4">
                <div className="flex flex-col gap-4">
                    <PopoverTitle>
                        {t("lomda-v3-editor.flip.manage-cards")}
                    </PopoverTitle>
                    <div className="flex flex-col gap-1">
                        {cards.map((c, i) => (
                            <div
                                key={c.id}
                                draggable
                                onDragStart={() => setDragIndex(i)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => {
                                    if (dragIndex !== null) move(dragIndex, i);
                                    setDragIndex(null);
                                }}
                                className="flex items-center gap-2.5 rounded-lg px-1 py-1.5"
                            >
                                <GripVertical className="size-5 shrink-0 cursor-grab text-muted-foreground" />
                                {c.icon ? (
                                    <TabIcon
                                        name={c.icon}
                                        className="size-4 shrink-0 text-primary"
                                    />
                                ) : (
                                    <span className="size-4 shrink-0" />
                                )}
                                <input
                                    value={c.title}
                                    onChange={(e) =>
                                        onChange(
                                            cards.map((x) =>
                                                x.id === c.id
                                                    ? { ...x, title: e.target.value }
                                                    : x
                                            )
                                        )
                                    }
                                    placeholder={t(
                                        "lomda-v3-editor.flip.card-title-ph"
                                    )}
                                    className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                                />
                                <button
                                    type="button"
                                    onClick={() =>
                                        onChange(
                                            cards.filter((x) => x.id !== c.id)
                                        )
                                    }
                                    className="flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground transition hover:text-destructive"
                                >
                                    <Trash2 className="size-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={() => onChange([...cards, createFlipCard()])}
                        className="flex items-center gap-2 self-start rounded-lg text-sm text-muted-foreground transition hover:text-primary"
                    >
                        <Plus className="size-4" />
                        {t("lomda-v3-editor.flip.add")}
                    </button>
                </div>
            </PopoverContent>
        </Popover>
    );
}

function ColumnsStepper({
    columns,
    onColumns,
    min,
    max,
}: {
    columns: number;
    onColumns: (n: number) => void;
    min: number;
    max: number;
}) {
    return (
        <div className="flex h-9 items-center gap-1 rounded-lg border border-border">
            <button
                type="button"
                aria-label="-"
                disabled={columns <= min}
                onClick={() => onColumns(Math.max(min, columns - 1))}
                className="flex size-8 items-center justify-center text-foreground transition hover:text-primary disabled:opacity-30"
            >
                <Minus className="size-4" />
            </button>
            <span className="w-6 text-center text-sm text-foreground">
                {columns}
            </span>
            <button
                type="button"
                aria-label="+"
                disabled={columns >= max}
                onClick={() => onColumns(Math.min(max, columns + 1))}
                className="flex size-8 items-center justify-center text-foreground transition hover:text-primary disabled:opacity-30"
            >
                <Plus className="size-4" />
            </button>
        </div>
    );
}

function AppearancePopover({
    columns,
    maxColumns,
    hiddenFields,
    onColumns,
    appearance,
    onChange,
    onReset,
}: {
    columns: number;
    maxColumns: number;
    hiddenFields: string[];
    onColumns: (n: number) => void;
    appearance: FlipCardsAppearance;
    onChange: (patch: Partial<FlipCardsAppearance>) => void;
    onReset: () => void;
}) {
    const { minColumns, appearanceGroups } = FLIP_CARDS_CONFIG;
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button type="button" className={cn(chromeBtn, chromeBtnOpen)}>
                    <SlidersHorizontal className="size-5" />
                    {t("lomda-v3-editor.flip.appearance")}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-[320px] rounded-[20px] border border-border bg-card p-4 shadow-md"
            >
                <div className="flex flex-col gap-4">
                    <PopoverTitle>
                        {t("lomda-v3-editor.flip.appearance")}
                    </PopoverTitle>

                    {appearanceGroups.map((group, gi) => (
                        <div key={gi} className="flex flex-col gap-4">
                            {gi > 0 && <AppearanceDivider />}
                            {group.map((field) => {
                                if (hiddenFields.includes(field.key))
                                    return null;
                                if (field.control === "columns") {
                                    return (
                                        <AppearanceRow
                                            key={field.key}
                                            label={t(field.labelKey)}
                                        >
                                            <ColumnsStepper
                                                columns={columns}
                                                onColumns={onColumns}
                                                min={minColumns}
                                                max={maxColumns}
                                            />
                                        </AppearanceRow>
                                    );
                                }
                                const apKey =
                                    field.key as keyof FlipCardsAppearance;
                                const value = appearance[apKey];
                                return (
                                    <AppearanceRow
                                        key={field.key}
                                        label={t(field.labelKey)}
                                    >
                                        {field.control === "switch" ? (
                                            <Switch
                                                checked={Boolean(value)}
                                                onCheckedChange={(v) =>
                                                    onChange({ [apKey]: v })
                                                }
                                            />
                                        ) : field.control === "borderWidth" ? (
                                            <div className="flex h-9 w-14 items-center justify-center rounded-lg border border-border bg-card px-2 text-sm text-foreground">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={8}
                                                    value={Number(value)}
                                                    onChange={(e) =>
                                                        onChange({
                                                            [apKey]: Math.max(
                                                                0,
                                                                parseInt(
                                                                    e.target
                                                                        .value,
                                                                    10
                                                                ) || 0
                                                            ),
                                                        })
                                                    }
                                                    className="w-6 bg-transparent text-center outline-none"
                                                />
                                                <span className="text-muted-foreground">
                                                    px
                                                </span>
                                            </div>
                                        ) : (
                                            <ColorChipField
                                                value={String(value)}
                                                onChange={(c) =>
                                                    onChange({ [apKey]: c })
                                                }
                                            />
                                        )}
                                    </AppearanceRow>
                                );
                            })}
                        </div>
                    ))}
                    <AppearanceReset
                        onReset={onReset}
                    />
                </div>
            </PopoverContent>
        </Popover>
    );
}
